import React, { useState, useEffect, useCallback } from 'react';
import { getAuthToken } from '../api/api';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useVistaEmpleado, recortarEmpleado, ALTO_FRANJA_SIMULACION } from '../context/VistaEmpleadoContext';
import { ChevronDown, X, LogOut } from 'lucide-react';
import { MENU_ERP } from '../utils/modulosErp';
import NotificacionesSidebar from './NotificacionesSidebar';
import { useGuia } from '../context/GuiaContext';
import { pasosBienvenida } from './guia/guionBienvenida';

const Sidebar = ({ onNavigate, isMobile }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  // "Simular vista empleado" (solo dirección): estado compartido con las páginas
  const { vistaEmpleado, simular, salir, simulando } = useVistaEmpleado();
  // Guía de bienvenida del empleado simulado en vista previa (recorre sus pantallas)
  const { estado: estadoGuia, iniciarGuia, pararGuia } = useGuia();
  
  const [empleados, setEmpleados] = useState([]);
  const [showEmpleadoSelector, setShowEmpleadoSelector] = useState(false);
  const [notificacionesCompras, setNotificacionesCompras] = useState(0);
  // Grupos colapsables del menú (ej: Marketing). Si el usuario no ha tocado
  // el toggle de un grupo, abre automáticamente el que contiene la ruta activa.
  const [expandedGroups, setExpandedGroups] = useState({});
  const toggleGroup = (key) => setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Fetch notificaciones de compras
  const fetchNotificacionesCompras = useCallback(async () => {
    try {
      const token = getAuthToken();
      if (!token) return;
      
      const response = await fetch(`${API_URL}/api/compras/notificaciones`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotificacionesCompras(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching notificaciones compras:', error);
    }
  }, [API_URL]);

  // Cargar notificaciones al inicio y cada 60 segundos
  useEffect(() => {
    fetchNotificacionesCompras();
    const interval = setInterval(fetchNotificacionesCompras, 60000);
    return () => clearInterval(interval);
  }, [fetchNotificacionesCompras]);

  const fetchEmpleados = useCallback(async () => {
    try {
      // Solo cargar empleados si es admin (tiene token normal, no empleado_token)
      const adminToken = localStorage.getItem('token');
      const empleadoToken = localStorage.getItem('empleado_token');
      
      // Si es un empleado logueado directamente, no intentar cargar la lista de empleados
      if (empleadoToken && !adminToken) {
        return;
      }
      
      if (!adminToken) return;
      
      const response = await fetch(`${API_URL}/api/empleados?activo=true`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEmpleados(data);
      }
    } catch (error) {
      console.error('Error fetching empleados:', error);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchEmpleados();
    
    const handlePermisosUpdate = (event) => {
      console.log('🔄 Permisos actualizados para empleado:', event.detail.empleadoId);
      fetchEmpleados();
    };
    
    window.addEventListener('empleadoPermisosUpdated', handlePermisosUpdate);
    
    return () => {
      window.removeEventListener('empleadoPermisosUpdated', handlePermisosUpdate);
    };
  }, [fetchEmpleados]);

  // Al simular se abre Mi Inicio del empleado (siempre lo tiene)
  const cambiarVistaEmpleado = (empleado) => {
    simular(empleado);
    setShowEmpleadoSelector(false);
    navigate('/dashboard-empleado');
    if (onNavigate) onNavigate();
  };

  const volverVistaAdmin = () => {
    if (estadoGuia?.previsualizar) pararGuia();
    salir();
    setShowEmpleadoSelector(false);
    if (onNavigate) onNavigate();
  };
  
  // Si cambian los permisos del empleado simulado, la vista los recoge
  useEffect(() => {
    if (vistaEmpleado && empleados.length > 0) {
      const empleadoActualizado = recortarEmpleado(empleados.find(e => e.id === vistaEmpleado.id));
      if (empleadoActualizado && JSON.stringify(empleadoActualizado) !== JSON.stringify(vistaEmpleado)) {
        simular(empleadoActualizado);
      }
    }
  }, [empleados, vistaEmpleado, simular]);

  // Menú común con la lista de permisos de la ficha del empleado (utils/modulosErp.js)
  const currentMenuItems = MENU_ERP;

  const hasAccess = (roles, modulePath, moduleId, hideForDireccion = false, alwaysShow = false) => {
    const currentUser = vistaEmpleado || user;
    
    // Si tiene alwaysShow, mostrar siempre para todos los usuarios
    if (alwaysShow) return true;
    
    // Si el usuario es dirección
    if (currentUser?.rol === 'direccion') {
      // Si este item debe ocultarse para dirección, no mostrar
      if (hideForDireccion) return false;
      // De lo contrario, tiene acceso a todo
      return true;
    }
    
    // Si el empleado tiene permisos_modulos definidos, usarlos (tienen prioridad)
    if (currentUser?.permisos_modulos && Array.isArray(currentUser.permisos_modulos) && currentUser.permisos_modulos.length > 0) {
      // Los permisos del empleado definen exactamente qué puede ver
      const moduleKey = moduleId || modulePath?.replace('/', '').replace('/', '-');
      return currentUser.permisos_modulos.includes(moduleKey);
    }
    
    // Si no tiene permisos específicos, usar el sistema de roles
    if (roles.includes('all')) return true;
    
    // Verificar si el rol del usuario está en los roles permitidos
    if (currentUser?.rol && roles.includes(currentUser.rol)) return true;
    
    return false;
  };

  // "Ver su guía": la guía real por las pantallas del simulado, en solo lectura
  const verGuia = () => {
    if (!vistaEmpleado) return;
    iniciarGuia({
      pasos: pasosBienvenida(vistaEmpleado.permisos_modulos || [], vistaEmpleado.rol, !!isMobile),
      previsualizar: true,
      nombre: vistaEmpleado.nombre,
    });
    if (onNavigate) onNavigate();
  };

  const isActive = (path) => location.pathname === path;

  const handleLinkClick = () => {
    if (onNavigate) onNavigate();
  };

  return (
    <div
      className="w-64 bg-white border-r border-gray-200 h-[100dvh] flex flex-col"
      style={simulando ? { height: `calc(100dvh - ${ALTO_FRANJA_SIMULACION}px)` } : undefined}
    >
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200 bg-white">
        <div className="flex items-center flex-1 justify-center">
          <img 
            src="/duetto-logo-negro.png" 
            alt="Duetto Catering" 
            className="h-12 object-contain"
          />
        </div>
        {isMobile && (
          <button onClick={onNavigate} className="text-gray-700 p-2.5 -mr-2" aria-label="Cerrar menú">
            <X className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* Selector de Empleado (Solo para admin) */}
      {user?.rol === 'direccion' && !vistaEmpleado && !isMobile && (
        <div className="p-3 border-b border-gray-200 bg-blue-50">
          <button
            onClick={() => setShowEmpleadoSelector(!showEmpleadoSelector)}
            className="w-full px-3 py-2 bg-white border border-blue-300 rounded-lg text-sm font-medium text-blue-700 hover:bg-blue-50 transition flex items-center justify-between"
          >
            <span>Simular Vista Empleado</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${showEmpleadoSelector ? 'rotate-180' : ''}`} />
          </button>
          
          {showEmpleadoSelector && (
            <div className="mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {empleados.map(emp => (
                <button
                  key={emp.id}
                  onClick={() => cambiarVistaEmpleado(emp)}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 transition border-b border-gray-100 last:border-b-0"
                >
                  <div className="font-medium text-gray-900">{emp.nombre}</div>
                  <div className="text-xs text-gray-500 capitalize">{emp.rol}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Vista Empleado Activa */}
      {vistaEmpleado && (
        <div className="p-3 border-b border-gray-200 bg-orange-50">
          <div className="bg-white border-2 border-orange-400 rounded-lg p-3">
            <div className="mb-2">
              <span className="text-xs font-semibold text-orange-700">VISTA EMPLEADO</span>
            </div>
            <div className="text-sm font-medium text-gray-900">{vistaEmpleado.nombre}</div>
            <div className="text-xs text-gray-500 capitalize">{vistaEmpleado.rol}</div>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <button
                type="button"
                onClick={verGuia}
                className="h-9 px-2 rounded-lg border border-orange-300 bg-white text-xs font-medium text-orange-700 hover:bg-orange-50"
                data-testid="vista-empleado-ver-tour"
              >
                Ver su guía
              </button>
              <button
                type="button"
                onClick={volverVistaAdmin}
                className="h-9 px-2 rounded-lg bg-orange-500 text-xs font-semibold text-white hover:bg-orange-600"
                data-testid="vista-empleado-salir"
              >
                Salir de la vista
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Datos del usuario (Mi Portal se retiró: todo está en Mi Inicio) */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-duetto-primary rounded-full flex items-center justify-center text-white font-semibold">
            {user?.nombre?.charAt(0)?.toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{user?.nombre}</p>
            <p className="text-xs text-gray-500 capitalize">{user?.rol}</p>
          </div>
        </div>
      </div>

      {/* Notificaciones - Posición destacada */}
      <div className="px-3 py-2 border-b border-gray-200">
        <NotificacionesSidebar collapsed={false} />
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        <div className="space-y-1 px-3">
          {currentMenuItems.map((item) => {
            // Los grupos (con children) NO se filtran por su propio moduleId: se
            // muestran si tienen al menos un hijo visible (ver más abajo). Solo
            // los items hoja se filtran aquí por rol/permiso.
            if (!item.children && !hasAccess(item.roles, item.path, item.moduleId, item.hideForDireccion, item.alwaysShow)) return null;

            const Icon = item.icon;

            // Grupo colapsable con hijos (ej: Marketing > Blog/Web/Instagram/Google Business)
            if (item.children) {
              const visibleChildren = item.children.filter((c) =>
                hasAccess(c.roles || item.roles, c.path, c.moduleId, c.hideForDireccion, c.alwaysShow)
              );
              if (visibleChildren.length === 0) return null;
              const anyChildActive = visibleChildren.some(
                (c) => location.pathname === c.path || location.pathname.startsWith(c.path + '/')
              );
              const userToggled = expandedGroups[item.label];
              const expanded = userToggled !== undefined ? userToggled : anyChildActive;
              return (
                <div key={item.label}>
                  <button
                    type="button"
                    onClick={() => toggleGroup(item.label)}
                    data-testid={`nav-group-${item.label.toLowerCase()}`}
                    data-tour={`menu-grupo-${item.moduleId}`}
                    className={`flex items-center justify-between w-full px-3 py-2.5 sm:py-2 rounded-lg text-sm font-medium transition-colors ${
                      anyChildActive ? 'bg-duetto-primary bg-opacity-10 text-duetto-primary' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <span className="flex items-center space-x-3">
                      <Icon className="w-5 h-5" />
                      <span>{item.label}</span>
                    </span>
                    <span className="flex items-center gap-2">
                      {!expanded && visibleChildren.some((c) => c.moduleId === 'compras') && notificacionesCompras > 0 && (
                        <span className="flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-xs font-bold rounded-full">
                          {notificacionesCompras > 99 ? '99+' : notificacionesCompras}
                        </span>
                      )}
                      <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                    </span>
                  </button>
                  {expanded && (
                    <div className="ml-8 mt-1 space-y-1">
                      {visibleChildren.map((child) => {
                        const childActive =
                          location.pathname === child.path || location.pathname.startsWith(child.path + '/');
                        return (
                          <Link
                            key={child.path}
                            to={child.path}
                            onClick={handleLinkClick}
                            data-testid={`nav-${child.label.toLowerCase().replace(/\s+/g, '-')}`}
                            data-tour={`menu-${child.moduleId}`}
                            className={`flex items-center justify-between px-3 py-2.5 sm:py-1.5 rounded-lg text-sm transition-colors ${
                              childActive
                                ? 'bg-duetto-primary text-white'
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            <span>{child.label}</span>
                            {child.moduleId === 'compras' && notificacionesCompras > 0 && (
                              <span className={`flex items-center justify-center min-w-[18px] h-5 px-1.5 text-xs font-bold rounded-full ${
                                childActive ? 'bg-white text-red-600' : 'bg-red-500 text-white'
                              }`}>
                                {notificacionesCompras > 99 ? '99+' : notificacionesCompras}
                              </span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            const active = isActive(item.path);

            if (item.external) {
              return (
                <a
                  key={item.path}
                  href={item.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleLinkClick}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  data-tour={`menu-${item.moduleId}`}
                  className="flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-gray-700 hover:bg-gray-100"
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </a>
              );
            }

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={handleLinkClick}
                data-testid={`nav-${item.label.toLowerCase()}`}
                data-tour={`menu-${item.moduleId}`}
                className={`
                  flex items-center space-x-3 px-3 py-2.5 sm:py-2 rounded-lg text-sm font-medium transition-colors relative
                  ${active ? 'bg-duetto-primary text-white' : 'text-gray-700 hover:bg-gray-100'}
                `}
              >
                <Icon className="w-5 h-5" />
                <span>{item.label}</span>
                {/* Indicador de notificaciones para Compras */}
                {item.moduleId === 'compras' && notificacionesCompras > 0 && (
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-xs font-bold rounded-full">
                    {notificacionesCompras > 99 ? '99+' : notificacionesCompras}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </nav>

      {/* Logout */}
      <div className="p-4 border-t border-gray-200">
        <button
          onClick={() => { logout(); if (onNavigate) onNavigate(); }}
          data-testid="logout-button"
          className="flex items-center space-x-3 w-full px-3 py-2 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
        >
          <LogOut className="w-5 h-5" />
          <span>Cerrar Sesión</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
